import { useQueryClient } from "@tanstack/react-query";
import {
  getQueryKey,
  useGqlMutation,
  useOrgId,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  threadMessagesQuery,
  threadMessagesVariables,
} from "@/app/Threads/GraphQL/threadMessageQueries";
import { addThreadNoteMutation } from "@/app/Threads/GraphQL/threadNoteMutations";
import { invalidateThreadQueries } from "@/app/Threads/utils/invalidateThreadQueries";
import { insertOptimisticMessage } from "@/app/Threads/utils/insertOptimisticMessage";
import { useUser } from "@/lib/Auth/useUser";
import { useActiveOrganisation } from "@/app/Organisations/Hooks/useActiveOrganisation";

export function useThreadNotes(threadId: string) {
  const queryClient = useQueryClient();
  const me = useUser();
  const { activeOrganisation } = useActiveOrganisation();
  const orgId = useOrgId();

  const messagesQueryKey = getQueryKey(
    threadMessagesQuery,
    threadMessagesVariables(threadId),
    orgId,
    true,
  );

  const addNote = useGqlMutation(addThreadNoteMutation, {
    onMutate: async (variables: any) => {
      await queryClient.cancelQueries({ queryKey: messagesQueryKey });

      return insertOptimisticMessage(queryClient, messagesQueryKey, {
        threadId,
        body: variables.body,
        author: me,
        organisation: activeOrganisation ?? null,
        isAdminNote: true,
      });
    },
    onError: (_err: any, _vars: any, context: any) => {
      if (context?.previous) {
        queryClient.setQueryData(messagesQueryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["ThreadMessages.Infinite"],
      });
      // A note leaves the thread where it was, but it is still the newest thing
      // in it, so the preview and the sort have to catch up.
      invalidateThreadQueries(queryClient);
    },
  });

  return { addNote };
}
